import {
  computed,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  DEFAULT_CUSTOM_TILE_SECTION,
  ICustomTile,
  ICustomTileSection,
} from '@web/dtos';
import { CustomTileSectionGateway } from './custom-tile-section.gateway';
import { IApiResponse } from '@web/shared-types';

@Injectable({
  providedIn: 'root',
})
export class CustomTileSectionStore {
  private readonly gateway: CustomTileSectionGateway =
    new CustomTileSectionGateway();
  readonly #customTileSectionsResource: ResourceRef<
    IApiResponse<ICustomTileSection[]> | undefined
  > = resource({
    loader: (): Promise<IApiResponse<ICustomTileSection[]>> => {
      return firstValueFrom(this.gateway.list());
    },
  });
  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#customTileSectionsResource.isLoading()
  );
  readonly customTileSections: Signal<ICustomTileSection[]> = computed(
    (): ICustomTileSection[] => {
      return this.#customTileSectionsResource.hasValue()
        ? this.#customTileSectionsResource.value()?.data &&
          this.#customTileSectionsResource.value()!.data!.length > 0
          ? this.#customTileSectionsResource.value()!.data!
          : [DEFAULT_CUSTOM_TILE_SECTION]
        : [DEFAULT_CUSTOM_TILE_SECTION];
    }
  );
  readonly activeSections: Signal<ICustomTileSection[]> = computed(() => {
    return this.customTileSections()
      .filter((sections: ICustomTileSection) => {
        return sections.tiles.find((tile: ICustomTile) => tile.isActive);
      })
      .map((section: ICustomTileSection) => {
        return {
          ...section,
          tiles: section.tiles.filter((tile: ICustomTile) => tile.isActive),
        };
      });
  });

  reload(): void {
    this.#customTileSectionsResource.reload();
  }

  updateCustomTileSection(query: ICustomTileSection[]): void {
    this.#customTileSectionsResource.update(
      (): IApiResponse<ICustomTileSection[]> => {
        return {
          data: query,
        };
      }
    );
  }

  save(query: ICustomTileSection[]): void {
    firstValueFrom(this.gateway.save(query)).then(
      (result: IApiResponse<ICustomTileSection[]>) => {
        if (result && result.data) {
          this.updateCustomTileSection(result.data);
        }
      }
    );
  }

  delete(sectionId: string | null): void {
    firstValueFrom(this.gateway.delete(sectionId)).then(
      (result: IApiResponse<ICustomTileSection[]>): void => {
        if (result && result.data) {
          this.updateCustomTileSection(result.data);
        }
      }
    );
  }
}
